import { z } from 'zod';

/* ---------- Tutorials (SPEC decision 20, api/openapi.yaml Tutorials) ---------- */
/*
 * The lessons arrive as Onboarding Studio published them, with media already signed by the API. Studio emits text
 * either as a plain string or per language (`{ en: '…' }`), so both shapes are accepted here and resolved to one
 * language by features/tutorials/tutorialsModel.ts. One malformed lesson is dropped, never the whole page.
 */

/** A string, or one string per language (`null` slots allowed). */
export const LocalizedText = z.union([z.string(), z.record(z.string(), z.string().nullable())]).nullish();
export type LocalizedText = z.infer<typeof LocalizedText>;

export const TutorialStepRaw = z.object({
  key: z.string().nullish(),
  kind: z.string().nullish(),
  title: LocalizedText,
  narration: LocalizedText,
  image: z.string().nullish(),
  audio: LocalizedText,
  durationMs: z.number().nullish(),
  /** A video lesson's chapter start (SPEC §38.2): the offset in the walkthrough video where this step's caption appears. */
  startMs: z.number().nullish(),
});
export type TutorialStepRaw = z.infer<typeof TutorialStepRaw>;

export const TutorialLessonRaw = z.object({
  id: z.string().min(1),
  audience: z.enum(['operator', 'client']).catch('client'),
  surface: z.string().nullish(),
  group: z.string().nullish(),
  icon: z.string().nullish(),
  title: LocalizedText,
  summary: LocalizedText,
  steps: z.array(TutorialStepRaw).catch([]),
  walkthrough: z.object({ video: z.string().nullish(), audio: LocalizedText }).nullish().catch(null),
});
export type TutorialLessonRaw = z.infer<typeof TutorialLessonRaw>;

/** Keeps the lessons that parse; a newer or broken lesson never hides the others. */
const Lessons = z.array(z.unknown()).transform((items) =>
  items.flatMap((item) => {
    const parsed = TutorialLessonRaw.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  }),
);

export const Tutorials = z.object({
  available: z.boolean(),
  version: z.string().nullable(),
  generatedAt: z.string().nullable(),
  defaultLang: z.string().catch('en'),
  languages: z.array(z.string()).catch(['en']),
  /** When the media URLs stop working; the console fetches the manifest again shortly before. */
  mediaExpiresAt: z.string(),
  lessons: Lessons,
});
export type Tutorials = z.infer<typeof Tutorials>;

/**
 * Media served by the API itself (a local Hangar) come as API-relative paths (`/v1/tutorials/media/…`); they are
 * resolved against this console's API address, which may differ from the public one the API knows. S3 links are kept.
 */
export const resolveMediaUrl = (url: string | null | undefined, apiBase: string): string | null | undefined =>
  url && url.startsWith('/') ? new URL(url, `${apiBase.replace(/\/+$/, '')}/`).href : url;

/* ---------- signed media lifetime ---------- */

/** Refetch this long before the signed media URLs expire, so a lesson never plays from a dead link. */
export const MEDIA_REFRESH_MARGIN_MS = 5 * 60_000;
/** Never poll faster than this, even when the expiry is near, past or skewed. */
export const MIN_REFETCH_MS = 30_000;
/** When the expiry is missing or unreadable: refetch every ten minutes. */
export const FALLBACK_REFETCH_MS = 10 * 60_000;

/** Milliseconds until the manifest must be fetched again: the media expiry minus a margin, clamped. Pure. */
export function refetchDelayMs(mediaExpiresAt: string | undefined, now: number): number {
  const expires = mediaExpiresAt ? Date.parse(mediaExpiresAt) : Number.NaN;
  if (!Number.isFinite(expires)) return FALLBACK_REFETCH_MS;
  return Math.max(MIN_REFETCH_MS, expires - now - MEDIA_REFRESH_MARGIN_MS);
}
